import { Text, useToast, useModal } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { useContext, useEffect, useRef } from 'react'
import {
  DisabledModal,
  Head,
  BillingAgreementMessage,
  ShippingSummary,
  OrderSummary,
  OAuthLoginConsentModal,
  PaymentOptionsErrorModal,
  BiometricRegistrationModal,
  SummaryCtaFooter,
  PPAModal,
  VerticalPaymentSchedule,
  CrossBorderHeader,
  SupHeader,
  GiftCardHeader,
  GiftCardSummaryCard,
  GiftCardPromoUnavailableMessage,
  GiftCardRecipientSection,
  SectionHeader,
  SummaryNotificationContext,
  AutopayModal,
  SummaryRouteGuard,
  NoErrorStatusGuard,
  StartedStatusGuard,
} from '../../component'
import { Route } from '../../config/router'
import { useFlag } from '@afterpaytouch/feature'
import {
  ApplePayStatus,
  CheckoutStatus,
  isSavedCard,
  PaymentMethodType,
  Features,
  getPayIn2Experiment,
  CheckoutPage,
  CreateDocumentsRequest,
  IdentityVerificationModalKey,
  AFFAutopayConfirmationModalKey,
  AddressModeType,
} from '../../model'
import { PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import {
  selectPurchaseMethod,
  useAppDispatch,
  useAppRouter,
  useApplePay,
  useApplePayState,
  useCheckoutError,
  useCheckoutState,
  useIsExpress,
  useIsReadyToConfirm,
  useIsShippingOptionValid,
  useSelectedPaymentMethod,
  useIsTrustedCheckout,
  useShouldShowCashAppTooltip,
  useOAuthAuthorizationFlow,
  useExperiment,
  shouldShowOAuthConsentForm,
  acceptOAuthConsent,
  declineOAuthConsent,
  setStartConfirmTime,
  resetCheckoutError,
  useIsUpdatePreferredPaymentDay,
  useCohort,
  useIsShippingAddressValid,
  useShippingAddress,
  useIsAfterpayFinanceFeeEligible,
  useShowAffIdvModal,
  useIsAfterpayAppGiftCardFlow,
  useIsGiftCardSummaryCardEnabled,
  useIsPickup,
  createDocuments,
  usePreferredCard,
  useInitialData,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { create } from 'react-modal-promise'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { usePostCheckoutTriggerExperiment } from '../../utils/post-checkout'
import { Cohort, FirstPaymentType } from '@afterpaytouch/portal-api'
import { IdentityVerificationModal } from '../../component/IdentityVerificationModal'
import { useGetIsAutoPayAgreed } from '../../component/AutopayToggle/hooks'
import { useConfirmCheckoutFinaliser } from '../../state/checkout/useConfirmCheckoutFinaliser'

export const ArcadeSummaryPage: CheckoutPage = () => {
  const { notification, setClicked, clicked } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { status, firstPaymentType, error, started } = useCheckoutState()
  const isExpress = useIsExpress()
  const isPickup = useIsPickup()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const showGiftCardSummaryCard = useIsGiftCardSummaryCardEnabled()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const { card: preferredCard, reason: preferredCardReason, valid: preferredCardValid } = usePreferredCard() ?? {}
  const hasSelectedPaymentMethod = selectedPaymentMethod !== undefined
  const hasSelectedNonCardPaymentMethod = hasSelectedPaymentMethod && selectedPaymentMethod.type !== PaymentMethodType.CREDIT_CARD
  const selectedSavedCardToken =
    hasSelectedPaymentMethod &&
    selectedPaymentMethod.type === PaymentMethodType.CREDIT_CARD &&
    selectedPaymentMethod.card !== undefined &&
    isSavedCard(selectedPaymentMethod.card)
      ? selectedPaymentMethod.card.token
      : undefined
  const hasSelectedDifferentSavedCard = selectedSavedCardToken !== undefined && selectedSavedCardToken !== preferredCard?.token
  const hasSelectedAlternativePaymentMethod = hasSelectedNonCardPaymentMethod || hasSelectedDifferentSavedCard
  const hasCardExpiryError = preferredCardReason === 'cardExpiry' && !preferredCardValid && !hasSelectedAlternativePaymentMethod
  const applepay = useApplePayState()
  const ready = useIsReadyToConfirm()
  const applePayHelper = useApplePay()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutError = useCheckoutError()
  const shouldUpdatePreferredPaymentDay = useIsUpdatePreferredPaymentDay()
  const { addressMode } = useInitialData()

  const isShippingOptionValid = useIsShippingOptionValid()
  const isShippingAddressValid = useIsShippingAddressValid()
  const { isCheckoutUpdated } = useShippingAddress()

  const { shouldShowCashAppAccountLinkingToast, saveToTags } = useShouldShowCashAppTooltip()
  const cashTooltipExperiment = useExperiment('CheckoutSummaryCashTooltip')

  const isSupIdvModalEnabled = useShowAffIdvModal()
  const { showModal: showSupIdvModal, closeModal: closeSupIdvModal } = useModal(IdentityVerificationModalKey)

  const isAFFSUPFlow = isAFF && !isAfterpayAppGiftCardFlow
  const isAFFGiftCardFlow = isAFF && isAfterpayAppGiftCardFlow

  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly
  const OAuthEnabledForUnicorn = useFlag(Features.OAUTH_FOR_UNICORN, false)
  const OAuthEnabledForCohort = (isCashOnlyCohort && OAuthEnabledForUnicorn) || !isCashOnlyCohort

  const autoPayAgreed = useGetIsAutoPayAgreed() ?? false
  const { visible: autopayConfirmationModalVisible, showModal: showAutopayConfirmationModal } = useModal(AFFAutopayConfirmationModalKey)

  const prevAutopayModalVisible = useRef(autopayConfirmationModalVisible)

  const handleAutopayConfirmationModalStatusChange = (value: boolean): void => {
    if (value) {
      setClicked(true)
      logEvent(TrackingEvent.PRESSED_AUTOPAY_ON_PAGE_CONFIRM_BUTTON)
      handleConfirm()
    }
  }

  useEffect(() => {
    try {
      const request: CreateDocumentsRequest = {
        autopay: autoPayAgreed,
      }
      dispatch(createDocuments(request))
    } catch {
      router.push(Route.TERMINAL_ERROR)
    }
  }, [])

  usePostCheckoutTriggerExperiment()

  const isSummaryButtonDisabled = (!ready || clicked || selectedPaymentMethod === undefined || notification?.severe || hasCardExpiryError) ?? false

  const payIn2ExperimentValue = useFlag(Features.CHECKOUT_PAY_IN_2_AMOUNT, -1)
  useExperiment('PayIn2Experiment', !isExpress, getPayIn2Experiment(payIn2ExperimentValue))

  const toast = useToast()

  useEffect(() => {
    const switchedToValidCard = selectedPaymentMethod?.type !== PaymentMethodType.CREDIT_CARD && preferredCard !== null && preferredCard?.error === null
    if (isAFF && switchedToValidCard) {
      dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard.token } }, false))
    }
  }, [isAFF])

  useEffect(() => {
    if (error !== undefined && error.extra !== undefined) {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  useEffect(() => {
    if (shouldShowCashAppAccountLinkingToast) {
      toast({
        message: () => (
          <>
            <Text size={textSize} bold>
              {t('common:firstPaymentWithCash.heading')}
            </Text>
            <Text size={textSize}>
              {cashTooltipExperiment.control ? t('common:firstPaymentWithCash.content') : t('common:firstPaymentWithCash.contentExperiment')}
            </Text>
          </>
        ),
        kind: 'Success',
        toastTime: 5000,
        swipeToDismiss: true,
      })
      saveToTags()
    }
  }, [shouldShowCashAppAccountLinkingToast, saveToTags])

  useEffect(() => {
    if (isSupIdvModalEnabled && status < CheckoutStatus.Confirming) {
      showSupIdvModal()
    } else {
      closeSupIdvModal()
    }
  }, [isSupIdvModalEnabled, showSupIdvModal])

  const { textSize } = useCheckoutSizes()
  const isTrusted = useIsTrustedCheckout()

  const { oAuthAuthorization } = useOAuthAuthorizationFlow()
  const showPersistentLoginConsentForm = create(({ isOpen, onResolve, onReject }) => (
    <OAuthLoginConsentModal show={isOpen} onDecline={onReject} onAccept={onResolve} />
  ))

  const analyticsFirstPaymentType = firstPaymentType !== undefined && firstPaymentType !== '' ? firstPaymentType : FirstPaymentType.PaymentUpfront
  const finaliseCheckout = useConfirmCheckoutFinaliser({
    t,
    isTrusted,
    oAuthAuthorization,
    shouldUpdatePreferredPaymentDay,
    onBeforeNavigate: () => setClicked(false),
  })

  const handleConfirmClick = async (): Promise<void> => {
    if (isSummaryButtonDisabled) {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED)
      return
    }

    if (isAFF && autoPayAgreed) {
      showAutopayConfirmationModal()
      return
    }

    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SUMMARY_CONFIRM_BUTTON, {
      paymentMethodType: PaymentMethodTypes[selectedPaymentMethod?.type as PaymentMethodType],
    })
    handleConfirm()
  }

  const handleConfirm = async (): Promise<void> => {
    dispatch(setStartConfirmTime(performance.now()))

    await dispatch(selectPurchaseMethod(selectedPaymentMethod))

    if (selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY) {
      applePayHelper.initialise(t, logEvent)
      try {
        await applePayHelper.startSession()
      } catch {
        return
      }
    }

    await finaliseCheckout()
  }

  const pageTitle = `${t('summary:reviewOrder')} | ${t('common:brandName')}`

  useEffect(() => {
    if (applepay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applepay.status])

  useEffect(() => {
    if (status >= CheckoutStatus.Started && !ready) {
      logEvent(TrackingEvent.CHECKOUT_CONFIRM_DISABLED, {
        checkoutStatus: status,
        checkoutError: checkoutError?.error,
        isShippingOptionValid,
        isShippingAddressValid,
        isCheckoutUpdated,
      })
    }
  }, [ready])

  useEffect(() => {
    const showOauthConsent = async (): Promise<void> => {
      const shouldShowOAuthConsent = await dispatch(shouldShowOAuthConsentForm)
      const shouldShowOauthConsentModal = shouldShowOAuthConsent && OAuthEnabledForCohort
      await (shouldShowOauthConsentModal
        ? showPersistentLoginConsentForm()
            .then(() => {
              dispatch(acceptOAuthConsent)
            })
            .catch(() => {
              dispatch(declineOAuthConsent)
            })
        : Promise.resolve())
    }
    showOauthConsent()
  }, [])

  useEffect(() => {
    if (prevAutopayModalVisible.current && !autopayConfirmationModalVisible) {
      setClicked(false)
    }
    prevAutopayModalVisible.current = autopayConfirmationModalVisible
  }, [autopayConfirmationModalVisible, setClicked])

  return (
    <SummaryRouteGuard>
      <BiometricRegistrationModal />
      <NoErrorStatusGuard>
        <Head title={pageTitle} />
        <PaymentOptionsErrorModal />
        <StartedStatusGuard>
          <CrossBorderHeader />
          {isAFFSUPFlow && <SupHeader />}
          {showGiftCardSummaryCard && <GiftCardSummaryCard />}
          {!showGiftCardSummaryCard && isAFFGiftCardFlow && <GiftCardHeader />}
          {!showGiftCardSummaryCard && !isAFFGiftCardFlow && <OrderSummary narrowMargin={true} />}
          <GiftCardPromoUnavailableMessage />
          {showGiftCardSummaryCard && <GiftCardRecipientSection />}
          {isExpress && addressMode !== AddressModeType.NO_ADDRESS && (
            <>
              <SectionHeader title={isPickup ? t('summary:pickup') : t('summary:delivery')} />
              <ShippingSummary />
            </>
          )}
          <VerticalPaymentSchedule testNameSpace='vertical-payment-schedule' />
          <BillingAgreementMessage />
          <PPAModal />
          <SummaryCtaFooter clicked={clicked} isSummaryButtonDisabled={isSummaryButtonDisabled} notification={notification} onConfirm={handleConfirmClick} />
          <TrackPageViewed eventProperties={{ firstPaymentType: analyticsFirstPaymentType, summaryPageVersion: 'ga launch v2' }} />
          {clicked && <DisabledModal />}
          <IdentityVerificationModal setClicked={setClicked} />
          <AutopayModal modalKey={AFFAutopayConfirmationModalKey} onAutopayStatusChange={handleAutopayConfirmationModalStatusChange} autoPayAgreed={false} />
        </StartedStatusGuard>
      </NoErrorStatusGuard>
    </SummaryRouteGuard>
  )
}
